import { Component, Inject, OnInit, ViewChild, OnDestroy } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ModalComponent } from "ng-modal-lib";
import { ToastrService } from "ngx-toastr";
import { AdminService } from "src/app/admin/admin.service";
import { IAgreementDetails } from "../../interfaces/Agreement";

interface DialogData {
  bid_selection_id: any;
  total_batches: any;
  total_parts: any;
  enquiry_id: any;
  customer?: string
  currency?:string
}

@Component({
  selector: "app-chat-agreement",
  templateUrl: "./chat-agreement.component.html",
  styleUrls: ["./chat-agreement.component.scss"],
})
export class ChatAgreementComponent implements OnInit, OnDestroy {
  @ViewChild(ModalComponent) modal: ModalComponent;
  activeTab: number = 0;
  currencySymbol:string;
  intervalId: any;
  isFirstCall = true;
  agreementDetails: IAgreementDetails[] = []
  totalParts: number
  agreementForm: FormGroup;
  constructor(
    private fb: FormBuilder,
    public dialogref: MatDialogRef<ChatAgreementComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private adminService: AdminService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.getAgreementInputs(this.data["bid_selection_id"]);
    this.currencySymbol = this.data.currency;
    this.agreementForm = this.fb.group({
      parts: this.fb.array([]),
    });

    // Set interval to fetch agreement inputs every 2 seconds
    // this.intervalId = setInterval(() => {
    //   this.getAgreementInputs(this.data["bid_selection_id"]);
    // }, 2000);
  }

  getAgreementInputs(bid_selection_id: any): void {
    this.adminService
      .getVendorOrderItemAgreementDetails(bid_selection_id)
      .subscribe(
        (res: any) => {
          this.agreementDetails = res.body
          this.totalParts = this.agreementDetails?.length;
          // Update customer fields for each part
          this.updateFields(this.agreementDetails);
        },
        (err) => {
          this.toastr.error(
            "Failed to load agreement details for the enquiry!"
          );
        }
      );
  }

  updateFields(data: IAgreementDetails[]): void {
    // Ensure the form array matches the incoming data length
    while (this.parts.length < data.length) {
      this.addPartWithBatches(
        data[this.parts.length]?.associated_costings?.length || 0,
        data[this.parts.length]?.part_id || '0'
      );
    }

    // Update fields for each part and its batches
    data.forEach((partData, partIndex) => {
      const part = this.parts.at(partIndex) as FormGroup;
      const batches = part.get("batches") as FormArray;

      partData.associated_costings.forEach(
        (batchData: any, batchIndex: number) => {
          const batchForm = batches.at(batchIndex) as FormGroup;

          if (batchForm) {
            const patchValues = this.isFirstCall
              ? {
                  id: batchData.id || null,
                  agreed_quantity: batchData.agreed_quantity || "",
                  total_unit_cost: batchData.total_unit_cost || "",
                  total_price:
                    batchData.agreed_quantity ??
                    0 * batchData.total_unit_cost ??
                    0,
                  actual_date: batchData.actual_date || "",
                  payment_terms: batchData.payment_terms || "",
                  shipping_terms: batchData.shipping_terms || "",
                  quality_standards: batchData.quality_standards || "",
                  inspection_and_acceptance:
                    batchData.inspection_and_acceptance || "",
                  customer_agreed_quantity:
                    batchData.customer_agreed_quantity || "",
                  customer_total_unit_cost:
                    batchData.customer_total_unit_cost || "",
                  customer_actual_date: batchData.customer_actual_date || "",
                  customer_payment_terms:
                    batchData.customer_payment_terms || "",
                  customer_shipping_terms:
                    batchData.customer_shipping_terms || "",
                  customer_quality_standards:
                    batchData.customer_quality_standards || "",
                  customer_inspection_and_acceptance:
                    batchData.customer_inspection_and_acceptance || "",
                }
              : {
                  customer_agreed_quantity:
                    batchData.customer_agreed_quantity || "",
                  customer_total_unit_cost:
                    batchData.customer_total_unit_cost || "",
                  customer_actual_date: batchData.customer_actual_date || "",
                  customer_payment_terms:
                    batchData.customer_payment_terms || "",
                  customer_shipping_terms:
                    batchData.customer_shipping_terms || "",
                  customer_quality_standards:
                    batchData.customer_quality_standards || "",
                  customer_inspection_and_acceptance:
                    batchData.customer_inspection_and_acceptance || "",
                };

            batchForm.patchValue(patchValues);
          }
        }
      );
    });

    // Mark as subsequent calls after the first update
    this.isFirstCall = false;
  }

  addPartWithBatches(batchCount: number, partId: string): void {
    const partForm = this.fb.group({
      part_id: [partId],
      batches: this.fb.array([]),
    });

    for (let i = 0; i < batchCount; i++) {
      this.addBatchToPart(partForm);
    }

    this.parts.push(partForm);
  }

  get parts(): FormArray {
    return this.agreementForm.get("parts") as FormArray;
  }

  addBatchToPart(part: FormGroup): void {
    const batches = part.get("batches") as FormArray;

    const batchForm = this.fb.group({
      id: ["", Validators.required],
      agreed_quantity: ["", Validators.required],
      total_unit_cost: ["", Validators.required],
      total_price: [{ value: "", disabled: true }],
      actual_date: ["", Validators.required],
      payment_terms: [""],
      shipping_terms: [""],
      quality_standards: [""],
      inspection_and_acceptance: [""],

      // Customer-specific fields
      customer_agreed_quantity: [""],
      customer_total_unit_cost: [""],
      customer_actual_date: [""],
      customer_payment_terms: [""],
      customer_shipping_terms: [""],
      customer_quality_standards: [""],
      customer_inspection_and_acceptance: [""],
    });

    // Subscribe to changes in agreed_quantity and total_unit_cost
    batchForm
      .get("agreed_quantity")
      ?.valueChanges.subscribe(() => this.updateTotalPrice(batchForm));
    batchForm
      .get("total_unit_cost")
      ?.valueChanges.subscribe(() => this.updateTotalPrice(batchForm));

    // Initialize totalPrice if patchPart values are provided
    this.updateTotalPrice(batchForm);

    batches.push(batchForm);
  }

  updateTotalPrice(batchForm: FormGroup): void {
    const quantity = Number(batchForm.get("agreed_quantity")?.value || 0);
    const unitPrice = Number(batchForm.get("total_unit_cost")?.value || 0);
    const totalPrice = quantity * unitPrice;
    batchForm.get("total_price")?.setValue(totalPrice, { emitEvent: false });
  }

  changeProjectTab(index: number) {
    this.activeTab = index;
  }

  show(): void {
    this.modal.show();
  }

  signOffAgreement() {
    console.log(this.agreementForm.value); // Logs data from each tab

    this.adminService
      .updateVendorOrderAgreementDetails(
        this.data["bid_selection_id"],
        this.agreementForm.value
      )
      .subscribe(
        (res) => {},
        (err) => {
          this.toastr.error("Failed to update the agreement details.");
        }
      );
  }

  getPartName(part: FormGroup) {
    const partId = part.get('part_id')?.value;
    const name = this.agreementDetails.filter((res) => res.part_id === partId.toString())[0].cleaned_model?.model_file;
    return name;
  }

  onBlur(): void {
    // Add your business logic here
    this.getAgreementInputs(this.data["bid_selection_id"]);
  }

  closeDialog() {
    this.dialogref.close();
  }

  ngOnDestroy(): void {
    // Clear the interval when the component is destroyed
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
